import React, { useEffect, useState } from 'react';
import { MessageSquare, CheckCircle, AlertCircle, Send, Save } from 'lucide-react';
import api from '../services/api';

/**
 * Slack Incoming Webhook configuration panel.
 *
 * Renders in two modes:
 *   scope="org"      → /slack/config (caller's own org). Used on the
 *                      customer console's Integrations tab.
 *   scope="internal" → /slack/internal-config (super_admin only). Used on
 *                      the admin console's Integrations tab.
 *
 * Both endpoints return the same shape, just different default event keys.
 * The form is shared; only the labels differ.
 */

const SCOPE_META = {
  org: {
    title: 'Slack — your hiring channel',
    desc: 'Get a ping in Slack when candidates finish interviews, when credits run low, and when payments land. Uses an Incoming Webhook — paste the URL Slack gives you under Apps → Incoming Webhooks → Add Configuration.',
    eventLabels: {
      interview_completed:  'Interview completed',
      candidate_high_score: 'Candidate scored high',
      quota_low:            'Credits running low',
      billing_topped_up:    'Credits topped up',
      team_member_added:    'Team member added',
    },
  },
  internal: {
    title: 'Slack — Rupadi internal operations',
    desc: 'Internal-only channel for operators. Pings on enterprise signups, support tickets, QA suite failures, and payment failures.',
    eventLabels: {
      enterprise_signup:     'New enterprise signup',
      qa_suite_failed:       'QA suite failed',
      support_ticket_opened: 'Support ticket opened',
      payment_failed:        'Payment failed',
    },
  },
};

export default function SlackPanel({ scope = 'org' }) {
  const meta = SCOPE_META[scope] || SCOPE_META.org;
  const endpoint = scope === 'internal' ? '/slack/internal-config' : '/slack/config';

  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [testing, setTesting]   = useState(false);
  const [error, setError]       = useState('');
  const [success, setSuccess]   = useState('');
  const [cfg, setCfg]           = useState({ webhook_url: '', channel_label: '', events: {}, configured: false });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(endpoint);
      setCfg(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not load Slack config');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [endpoint]);

  const save = async () => {
    setError(''); setSuccess(''); setSaving(true);
    try {
      const r = await api.patch(endpoint, {
        webhook_url: cfg.webhook_url || '',
        channel_label: cfg.channel_label || '',
        events: cfg.events,
      });
      setCfg(r.data);
      setSuccess('Saved.');
      setTimeout(() => setSuccess(''), 2000);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not save');
    } finally { setSaving(false); }
  };

  const sendTest = async () => {
    setError(''); setSuccess(''); setTesting(true);
    try {
      await api.post('/slack/test', { webhook_url: cfg.webhook_url, scope });
      setSuccess('Test message sent — check your Slack channel.');
      setTimeout(() => setSuccess(''), 3500);
    } catch (e) {
      setError(e.response?.data?.detail || 'Test message failed');
    } finally { setTesting(false); }
  };

  const toggle = (k) => setCfg({ ...cfg, events: { ...cfg.events, [k]: !cfg.events[k] } });

  return (
    <div className="rounded-2xl border border-white/10 bg-ink-900 p-6">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-[#4A154B] text-white">
            <MessageSquare size={18} />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">{meta.title}</h3>
            <p className="mt-1 text-sm text-ink-400">{meta.desc}</p>
          </div>
        </div>
        <span className={`mt-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ${cfg.configured ? 'bg-emerald-500/15 text-emerald-300' : 'bg-white/5 text-ink-500'}`}>
          {cfg.configured ? <><CheckCircle size={12} /> Connected</> : 'Not connected'}
        </span>
      </div>

      {error && (
        <div className="mb-3 flex items-center gap-2 rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300">
          <AlertCircle size={14} /> {error}
        </div>
      )}
      {success && (
        <div className="mb-3 flex items-center gap-2 rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm text-emerald-300">
          <CheckCircle size={14} /> {success}
        </div>
      )}

      {loading ? (
        <p className="text-sm text-ink-400">Loading…</p>
      ) : (
        <>
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-ink-400">Webhook URL</label>
              <input
                type="text"
                value={cfg.webhook_url || ''}
                onChange={(e) => setCfg({ ...cfg, webhook_url: e.target.value })}
                placeholder="https://hooks.slack.com/services/T0…/B0…/…"
                className="input font-mono text-xs"
              />
              <p className="mt-1 text-xs text-ink-500">
                Slack → Apps → Incoming Webhooks → Add Configuration → pick a channel → copy the URL here.
              </p>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-ink-400">Channel label (cosmetic)</label>
              <input
                type="text"
                value={cfg.channel_label || ''}
                onChange={(e) => setCfg({ ...cfg, channel_label: e.target.value })}
                placeholder="#hiring"
                className="input"
              />
            </div>
          </div>

          <div className="mt-5">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-ink-400">Events to send</p>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {Object.entries(meta.eventLabels).map(([key, label]) => (
                <label key={key} className="flex cursor-pointer items-center gap-2 rounded-lg border border-white/10 bg-ink-950 p-3 text-sm hover:bg-white/5">
                  <input
                    type="checkbox"
                    checked={!!cfg.events?.[key]}
                    onChange={() => toggle(key)}
                    className="h-4 w-4 accent-brand-500"
                  />
                  <span className="text-ink-200">{label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-2">
            <button
              onClick={save}
              disabled={saving}
              className="btn-primary inline-flex items-center gap-2"
            >
              <Save size={14} /> {saving ? 'Saving…' : 'Save changes'}
            </button>
            <button
              onClick={sendTest}
              disabled={testing || !cfg.webhook_url}
              className="btn-secondary inline-flex items-center gap-2"
            >
              <Send size={14} /> {testing ? 'Sending…' : 'Send test message'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
